import {
  type SharedContentItem,
  type Thread,
  useStores,
} from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { SidebarItemMenuItems } from '@/components/blocks/Layout/Sidebar/SidebarItemMenuItems';
import { IconButton } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { getArchiveErrorDescription } from '@/features/chat/archive-error';
import { MeetingNoteDropdownItems } from '@/features/meetings/meetings-list';
import { confirmChatListDeletion } from '@/features/settings/proactive-agent-chat-deletion';
import { useTabs } from '@/features/tabs';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { inElectron } from '@/utils/capabilities';

import { MoveToProjectDropdownSubMenu } from './move-to-project';

export const ProjectItemRowActions = observer(function ProjectItemRowActions({
  item,
  isOwn,
  itemType,
  href,
  isOpen,
  onOpenChange,
  onRename,
}: {
  item: SharedContentItem;
  isOwn: boolean;
  itemType: 'chat' | 'meeting';
  href: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onRename: () => void;
}) {
  const { threads } = useStores();
  const thread = itemType === 'chat' ? threads.getById(item.id) : undefined;
  const canAct = isOwn && (itemType === 'meeting' || thread !== undefined);

  const hasActions = canAct || (itemType === 'chat' && inElectron());
  if (!hasActions) return null;

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild>
        <IconButton
          icon={Icons.moreHorizontal}
          size="default"
          variant="secondary"
          aria-label={`More actions for ${item.title}`}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 opacity-0',
            'data-[state=open]:opacity-100 data-[state=open]:bg-secondary-hover',
            'touch:opacity-100',
            'group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100',
            'transition-opacity duration-150'
          )}
          onClick={(event) => event.stopPropagation()}
        />
      </DropdownMenu.Trigger>
      {isOpen && (
        <DropdownMenu.Content
          align="end"
          className="min-w-36"
          fit
          onCloseAutoFocus={(event) => event.preventDefault()}
          onClick={(event) => event.stopPropagation()}
        >
          {itemType === 'meeting' ? (
            <MeetingNoteDropdownItems meetingId={item.id} />
          ) : (
            <ChatItems
              item={item}
              thread={canAct ? thread : undefined}
              href={href}
              onRename={onRename}
            />
          )}
        </DropdownMenu.Content>
      )}
    </DropdownMenu.Root>
  );
});

const ChatItems = observer(function ChatItems({
  item,
  thread,
  href,
  onRename,
}: {
  item: SharedContentItem;
  thread: Thread | undefined;
  href: string;
  onRename: () => void;
}) {
  const { threads } = useStores();
  const { openTab } = useTabs();

  return (
    <SidebarItemMenuItems
      variant="dropdown"
      title={item.title}
      isFavorited={thread?.starred}
      onOpenInTab={() => openTab({ path: href })}
      onRename={thread ? onRename : undefined}
      onFavorite={
        thread
          ? () => {
              threads.updateThread(thread, { starred: !thread.starred });
              trackEvent('CHAT_STARRED', {
                chatId: thread.id,
                favorite: !thread.starred,
              });
            }
          : undefined
      }
      onArchive={
        thread
          ? async () => {
              try {
                await threads.archiveThread(thread);
                trackEvent('CHAT_ARCHIVED', { chatId: thread.id });
              } catch (error) {
                toast.error('Failed to archive chat', {
                  description: getArchiveErrorDescription(error, 'archive'),
                });
              }
            }
          : undefined
      }
      onDelete={
        thread
          ? async () => {
              if (await confirmChatListDeletion([thread.id]))
                await threads.delete(thread);
            }
          : undefined
      }
      extra={
        thread ? <MoveToProjectDropdownSubMenu threadId={item.id} /> : undefined
      }
    />
  );
});
